import { FC, useState } from 'react';

import { useForm } from '@inertiajs/react';
import { format, startOfToday } from 'date-fns';
import { uk } from 'date-fns/locale';
import {
    Calendar as CalendarIcon,
    CalendarOff,
    ChevronDown,
    ChevronUp,
    Save,
    Undo2,
} from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { Calendar } from '../../components/ui/Calendar';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../components/ui/Card';
import { Label } from '../../components/ui/Label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../../components/ui/Popover';
import { profileAvailabilityRoute } from '../../models/routes';
import { EditUser } from '../../types';
import { cn, formatLocalDateTime } from '../../utils';

interface Props {
    user: EditUser;
    isOpen: boolean;
    onToggle: () => void;
    error?: string;
}

type AvailabilityFormValues = {
    paused_until: string;
};

const toDateInputValue = (date?: Date | string | null) => {
    if (!date) return '';

    const parsedDate = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return '';

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

const dateInputValueToDate = (value: string) => {
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return undefined;

    return new Date(year, month - 1, day);
};

export const SellerAvailabilityForm: FC<Props> = ({
    user,
    isOpen,
    onToggle,
    error,
}) => {
    const isPaused = Boolean(user.seller_paused);
    const { data, setData, patch, processing, errors } =
        useForm<AvailabilityFormValues>({
            paused_until: toDateInputValue(user.seller_paused_until),
        });
    const resumeForm = useForm({});
    const [calendarOpen, setCalendarOpen] = useState(false);
    const selectedDate = dateInputValueToDate(data.paused_until);

    const handleSave = () => {
        patch(profileAvailabilityRoute(), {
            preserveScroll: true,
        });
    };

    const handleResume = () => {
        resumeForm.delete(profileAvailabilityRoute(), {
            preserveScroll: true,
        });
    };

    return (
        <Card className="h-fit" data-testid="seller-availability-form">
            <CardHeader
                className="cursor-pointer transition-colors hover:bg-muted/50"
                onClick={onToggle}
            >
                <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <CalendarOff className="h-5 w-5 text-primary" />
                        Доступність
                    </div>
                    {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                </CardTitle>
                <CardDescription>
                    Тимчасово призупиніть нові замовлення
                </CardDescription>
            </CardHeader>

            {isOpen && (
                <CardContent className="space-y-4">
                    {isPaused && user.seller_paused_until && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-500/10 dark:text-amber-100">
                            Профіль призупинено до{' '}
                            {formatLocalDateTime(user.seller_paused_until)}.
                            Premium-пропозиції заморожені на час паузи.
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="seller-paused-until">
                            Призупинити до
                        </Label>
                        <Popover
                            open={calendarOpen}
                            onOpenChange={setCalendarOpen}
                        >
                            <PopoverTrigger asChild>
                                <Button
                                    id="seller-paused-until"
                                    type="button"
                                    variant="outline"
                                    className={cn(
                                        'h-11 w-full justify-start text-left text-base font-normal',
                                        !selectedDate &&
                                            'text-muted-foreground',
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {selectedDate
                                        ? format(selectedDate, 'dd.MM.yyyy', {
                                              locale: uk,
                                          })
                                        : 'Оберіть дату'}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                align="start"
                                className="w-auto p-0"
                            >
                                <Calendar
                                    mode="single"
                                    selected={selectedDate}
                                    defaultMonth={selectedDate ?? startOfToday()}
                                    disabled={{ before: startOfToday() }}
                                    locale={uk}
                                    weekStartsOn={1}
                                    autoFocus
                                    onSelect={(date) => {
                                        if (!date) return;

                                        setData(
                                            'paused_until',
                                            toDateInputValue(date),
                                        );
                                        setCalendarOpen(false);
                                    }}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    {(error || errors.paused_until) && (
                        <p className="text-sm text-red-500">
                            {error || errors.paused_until}
                        </p>
                    )}

                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                            type="button"
                            onClick={handleSave}
                            disabled={processing || !data.paused_until}
                            className="flex-1"
                        >
                            <Save className="mr-2 h-4 w-4" />
                            {processing
                                ? 'Збереження...'
                                : isPaused
                                  ? 'Змінити дату'
                                  : 'Призупинити профіль'}
                        </Button>

                        {isPaused && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleResume}
                                disabled={resumeForm.processing}
                                className="flex-1"
                            >
                                <Undo2 className="mr-2 h-4 w-4" />
                                Відновити зараз
                            </Button>
                        )}
                    </div>
                </CardContent>
            )}
        </Card>
    );
};
